import React, { useState, useContext, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { SunIcon, MoonIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid';
import gt_logo from '../imgs/logo_transparent_background_edit.png';
import TimerButton from './TimerButton';

const Logo = () => (
  <div className="flex items-center">
    <img src={gt_logo} alt="GrayTeck Logo" className="h-8 w-auto mr-2" />
    <span className="font-bold text-xl text-grayteck-900 dark:text-grayteck-100 hidden sm:inline">GrayTeck</span>
  </div>
);

const ThemeToggle = () => {
  const { darkMode, toggleDarkMode } = useContext(ThemeContext);
  return (
    <button
      onClick={toggleDarkMode}
      className="p-2 rounded-full bg-grayteck-200 dark:bg-darkbg-600 hover:bg-grayteck-300 dark:hover:bg-darkbg-500 transition-colors duration-200"
    >
      {darkMode ? (
        <SunIcon className="h-5 w-5 text-yellow-500" />
      ) : (
        <MoonIcon className="h-5 w-5 text-grayteck-800" />
      )}
    </button>
  );
};

const navLinkClass = ({ isActive }) =>
  `block px-3 py-2 rounded-md text-base font-medium ${
    isActive
      ? 'bg-grayteck-300 dark:bg-darkbg-700 text-grayteck-900 dark:text-grayteck-100'
      : 'text-grayteck-700 dark:text-grayteck-200 hover:bg-grayteck-200 dark:hover:bg-darkbg-700'
  }`;

const Navbar = ({ user, onLogout, onAddClient }) => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const nav = useNavigate();
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="bg-grayteck-100 dark:bg-darkbg-800 relative z-50">
      {/* Main navbar row */}
      <div className="px-4 py-3 flex justify-between items-center">
        {/* Left: Logo */}
        <div className="flex items-center">
          <Logo />
        </div>

        {/* Center: Desktop nav links */}
        <nav className="hidden md:flex items-center space-x-1">
          <NavLink to="/" end className={navLinkClass}>Home</NavLink>
          <NavLink to="/clients" className={navLinkClass}>Clients</NavLink>
          <NavLink to="/create" className={navLinkClass}>Create Document</NavLink>
        </nav>

        {/* Right: actions */}
        <div className="flex items-center gap-2">
          {/* Timer - icon only on mobile */}
          <div className="hidden sm:block">
            <TimerButton />
          </div>
          <div className="sm:hidden">
            <TimerButton compact />
          </div>

          {/* Welcome text - hidden on mobile */}
          <span className="hidden lg:block text-sm text-grayteck-800 dark:text-grayteck-200 whitespace-nowrap">
            Welcome, {user?.username}!
          </span>

          {/* Desktop dropdown (three dots) */}
          <div className="hidden md:block relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!isDropdownOpen)}
              className="p-2 rounded-full hover:bg-grayteck-200 dark:hover:bg-darkbg-700 transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 text-grayteck-600 dark:text-grayteck-200">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zm0 5.25a.75.75 0 110-1.5.75.75 0 010 1.5zm0 5.25a.75.75 0 110-1.5.75.75 0 010 1.5z" />
              </svg>
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-darkbg-700 rounded-md shadow-lg z-50 py-1">
                <button
                  onClick={() => { setDropdownOpen(false); onAddClient(); }}
                  className="block w-full px-4 py-2 text-left text-sm text-grayteck-700 dark:text-grayteck-200 hover:bg-grayteck-100 dark:hover:bg-darkbg-600"
                >
                  Create New Client
                </button>
                <button
                  onClick={() => { setDropdownOpen(false); nav('/settings'); }}
                  className="block w-full px-4 py-2 text-left text-sm text-grayteck-700 dark:text-grayteck-200 hover:bg-grayteck-100 dark:hover:bg-darkbg-600"
                >
                  Settings
                </button>
                <button
                  onClick={() => { setDropdownOpen(false); onLogout(); }}
                  className="block w-full px-4 py-2 text-left text-sm text-grayteck-700 dark:text-grayteck-200 hover:bg-grayteck-100 dark:hover:bg-darkbg-600"
                >
                  Logout
                </button>
              </div>
            )}
          </div>

          <ThemeToggle />

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-md hover:bg-grayteck-200 dark:hover:bg-darkbg-700 transition-colors"
            onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6 text-grayteck-700 dark:text-grayteck-200" />
            ) : (
              <Bars3Icon className="h-6 w-6 text-grayteck-700 dark:text-grayteck-200" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="md:hidden border-t border-grayteck-200 dark:border-darkbg-600 bg-grayteck-100 dark:bg-darkbg-800 absolute left-0 right-0 shadow-lg z-50"
        >
          <div className="px-4 py-3 space-y-1">
            {/* User greeting */}
            <div className="px-3 py-2 text-sm text-grayteck-500 dark:text-grayteck-400 border-b border-grayteck-200 dark:border-darkbg-600 mb-2">
              Welcome, {user?.username}!
            </div>

            {/* Nav links */}
            <NavLink to="/" end className={navLinkClass} onClick={closeMobileMenu}>Home</NavLink>
            <NavLink to="/clients" className={navLinkClass} onClick={closeMobileMenu}>Clients</NavLink>
            <NavLink to="/create" className={navLinkClass} onClick={closeMobileMenu}>Create Document</NavLink>

            {/* Divider */}
            <div className="border-t border-grayteck-200 dark:border-darkbg-600 my-2" />

            {/* Action items */}
            <button
              onClick={() => { closeMobileMenu(); onAddClient(); }}
              className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-grayteck-700 dark:text-grayteck-200 hover:bg-grayteck-200 dark:hover:bg-darkbg-700"
            >
              Create New Client
            </button>
            <button
              onClick={() => { closeMobileMenu(); nav('/settings'); }}
              className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-grayteck-700 dark:text-grayteck-200 hover:bg-grayteck-200 dark:hover:bg-darkbg-700"
            >
              Settings
            </button>
            <button
              onClick={() => { closeMobileMenu(); onLogout(); }}
              className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
